/* eslint-disable @nx/enforce-module-boundaries */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  Inject,
  Injectable,
  signal,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  delay,
  Observable,
  of,
  ReplaySubject,
  take,
  tap,
  throwError,
} from 'rxjs';
import { ApiRouter } from '../global/api-routes';
import { ClaimSteps } from '../statics/claim-steps.enum';
import { getRoute } from '../statics/steps-route-mapping';
import { CONFIG } from '@domgen/dgx-fe-config';
import { AppConfig } from '@domgen/claims/feature-manage-my-repair';
import { engineerBookingMock } from '../api-mocks/book-enginner';
import { ClaimQuestion } from '../types/orbit-question';
import { de, is } from 'date-fns/locale';

@Injectable({
  providedIn: 'root',
})
export class OrbitFormClaimService {
  plan: WritableSignal<any | undefined> = signal(undefined);
  currentStep = signal(ClaimSteps.CaptureModelNumber);
  planNumber = signal<string | undefined>(undefined);
  formValue: any = {};
  initialFormValue = new ReplaySubject<Record<string, any>>(1);
  stillCantFindNumber = signal(false);
  claimData = signal(undefined);
  selectedFaultDate = signal<string | undefined>(undefined);
  errorMessage = signal<string | undefined>(undefined);
  modelNumber = signal<any | undefined>(undefined);
  dropdownOptions = signal<any>(true);
  engineerBookingDetails = signal<any>(engineerBookingMock);
  currentQuestion = signal<ClaimQuestion | undefined>(undefined);
  claimId = signal<string | undefined>(undefined);
  personalDetails = signal<any | undefined>(undefined);
  readonly formSubmitting = signal(false);
  readonly isLoading = signal(false);

  // placeholder until we manage this somewhere else, probably best on the BE.
  private autoQuestionResponse = [
    {
      text: '<p>Accessory / Breakdown</p>',
      answer: 'Breakdown',
    },
    {
      text: 'Comments Box',
      answer: 'Display Response Field Only',
      textAnswer: 'na',
    },
  ];

  updateModelNumberState(state: boolean) {
    this.stillCantFindNumber.set(state);
  }

  updateModelNumber(modelValue: any) {
    this.errorMessage.set(undefined);
    this.modelNumber.set(modelValue);
  }

  private claimState = computed(() => {
    return {
      stillCantFindNumber: this.stillCantFindNumber(),
      claimData: this.claimData(),
      planNumber: this.planNumber() as string,
      isLoading: this.isLoading(),
      currentStep: this.currentStep(),
      percentage: Math.floor(
        (this.currentStep() /
          Object.keys(ClaimSteps).filter((k) => isNaN(+k)).length) *
          100
      ),
      selectedFaultDate: this.selectedFaultDate(),
      errorMessage: this.errorMessage(),
      submittingValue: this.formSubmitting(),
      modelNumber: this.modelNumber(),
      engineerBookingDetails: this.engineerBookingDetails(),
      currentQuestion: this.currentQuestion(),
      claimId: this.claimId(),
      personalDetails: this.personalDetails(),
    };
  });

  get state() {
    return this.claimState();
  }

  loadPlanClaims(planNumber: string) {
    this.planNumber.set(planNumber);
    this.getPlan();
    this.getPersonalDetails();
  }

  getPlan() {
    this.http
      .post(ApiRouter.getMyAccountPlanDetails(), {
        plan_number: this.planNumber(),
      })
      .subscribe((res: any) => {
        this.plan.set(res.result);
      });
  }

  // TODO move this url to config
  getPersonalDetails() {
    this.http
      .get(`${this.config.commonPersonServiceUrl}/get-person-details`)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.personalDetails.set(res.result.person);
      });
  }

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    @Inject(CONFIG) private readonly config: AppConfig
  ) {}

  reset(): void {
    this.formValue = {};
    this.initialFormValue.next(this.formValue);
  }

  setPlanNumber(planNumber: string) {
    if (!planNumber) return;
    this.planNumber.set(planNumber);
  }

  nextPage() {
    const nextStep = (this.currentStep() + 1) as number;
    if (ClaimSteps[nextStep]) {
      this.currentStep.set(nextStep);
      this.router.navigate([
        'orbit',
        this.state.planNumber,
        getRoute(this.currentStep()),
      ]);
    }
  }

  inspectBundle(bundle: string) {
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/inspect-bundle`;
    const request = {
      bundle,
    };

    return this.http.post(url, request).pipe(
      tap((val) => {
        this.claimData.set(val);
      })
    );
  }

  updatTransaction(body: any) {
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/UpdateTransaction`;
    return this.http.post(url, body);
  }

  putQuestion(body: any) {
    this.isLoading.set(true);
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/PutAnswer`;
    this.http
      .post(url, body)
      .pipe(take(1))
      .subscribe((val: any) => this.handleQuestionResponse(val));
  }

  handleQuestionResponse(val: any) {
    // do we have a valid question
    if (val?.nextQuestionID || val?.questionID) {
      this.currentQuestion.set(val as ClaimQuestion);
      // i hate this! There's no way to determine what question is the fault date question as id's change.
      // this could breka the whole app if the config changes the text
      if (val.questionLabel === 'When did the fault happen?') {
        this.setPage(ClaimSteps.FaultStartDate);
        this.isLoading.set(false);
      } else {
        // pass the question label & answer data to find out if we can auto complete - again better on the BE
        const isAutoAnswer = this.checkCanAutoComplete(
          val.questionLabel,
          val.answerData
        );
        if (isAutoAnswer) {
          const body = {
            claimID: this.state.claimId,
            QuestionID: this.state.currentQuestion.questionID,
            ...isAutoAnswer,
          };
          this.putQuestion(body);
        } else {
          // if we cant auto complete we need to ask this question
          this.setPage(ClaimSteps.AdditionalInfo);
          this.isLoading.set(false);
        }
      }
    } else {
      // check if we went backwards to answer any additional questions, if so we need to force to validate details
      if (
        this.selectedFaultDate() &&
        this.currentStep() === ClaimSteps.AdditionalInfo
      ) {
        this.setPage(ClaimSteps.ValidateDetails);
      } else {
        this.nextPage();
      }
      this.isLoading.set(false);
    }
  }

  checkCanAutoComplete(text: string, answerData: any[]) {
    const response = this.autoQuestionResponse.find((x) => x.text === text);
    if (response) {
      return {
        answerID: answerData.find((x) => x.answerLabel === response.answer)
          .answerID,
        answerValue: response.textAnswer ?? response.answer,
      };
    }
    return response;
  }

  handleFaultDateQuestion() {
    const faultDateAnswer = {
      claimID: this.state.claimId,
      QuestionID: this.state.currentQuestion.questionID,
      AnswerID: this.state.currentQuestion.answerData[0].answerID,
      AnswerValue: new Date(this.state.selectedFaultDate),
    };
    this.putQuestion(faultDateAnswer);
  }

  // this needs work - it needs to actually pull data from the right places, not hardcoded
  // remove the hardcoded fault codes when fault select has been built.
  submitModelNumber() {
    this.isLoading.set(true);
    return this.updatTransaction({
      ClaimTypeID: '3',
      GUID: this.claimData()?.startTransaction.gUID,
      Manufacturer: this.plan()?.plan?.item?.manufacturer,
      FaultCategoryID: 163,
      FaultID: '26441',
      ModelNumber: this.modelNumber()?.title,
    })
      .pipe(take(1))
      .subscribe((val: any) => {
        this.claimId.set(val.claimID);
        this.currentQuestion.set(val as ClaimQuestion);
        this.isLoading.set(false);
        this.handleQuestionResponse(val);
      });
  }

  handleNextNav() {
    this.isLoading.set(true);
    switch (this.currentStep()) {
      case ClaimSteps.CaptureModelNumber:
        if (!this.modelNumber() && !this.stillCantFindNumber()) {
          this.errorMessage.set('Please enter a model number.');
          this.isLoading.set(false);
          return false;
        }
        this.nextPage();
        this.isLoading.set(false);
        break;
      case ClaimSteps.FaultCapture:
        this.submitModelNumber();

        break;
      case ClaimSteps.FaultStartDate:
        if (this.selectedFaultDate() == undefined) {
          this.errorMessage.set('Select a date to continue.');
          this.isLoading.set(false);
          return false;
        }
        this.handleFaultDateQuestion();
        break;
      case ClaimSteps.ValidateDetails:
        this.getServiceOptions();
        break;
    }
  }

  setPage(nextStep: ClaimSteps) {
    this.currentStep.set(nextStep);
    this.router.navigate([
      'orbit',
      this.state.planNumber,
      getRoute(this.currentStep()),
    ]);
  }

  getServiceOptions() {
    const url = `${this.config.brightClaimsAPI}/api/v1/orbit/GetServiceOption`;
    const body = {
      ClaimID: this.state.claimId,
      CustomerFirstName: this.state.personalDetails.personalDetails.firstName,
      CustomerLastName: this.state.personalDetails.personalDetails.surname,
      CustomerTitle: this.state.personalDetails.personalDetails.title,
      CustomersEmail: this.state.personalDetails.contact.email,
      CustomersHouseStreetName: 'Priestgate',
      CustomersLandLine:
        this.state.personalDetails.contact.landline ?? '01202 000000',
      CustomersLocalArea: '',
      CustomersMobile:
        this.state.personalDetails.contact.mobile ?? '07999999999',
      CustomersPostCode: 'PE1 1LF',
      CustomersTownCity: 'Peterborough',
      GUID: this.state.claimData.startTransaction.gUID,
    };
    // NEEDS LOOKING AT - NOT SURE WHAT API IS EXPECTING - BOOKING
    // debugger;
    // this.http.post(url, body).subscribe((val: any) => {
    //   debugger;
    // });
    this.nextPage();
    this.isLoading.set(false);
  }
}
